/**
 * Cookie Consent Utilities
 *
 * Provides utilities to read and write cookie consent preferences.
 *
 * Cookie format:
 * - pressidium_cookie_consent: JSON with categories array (e.g., {"categories":["necessary","analytics","marketing","preferences"]})
 *   This matches the WordPress Pressidium plugin format for seamless cross-domain consent.
 *
 * Future: pp_user_consent can be used as an alternative if needed.
 */

// Cookie consent categories
export type ConsentCategory = "necessary" | "analytics" | "marketing" | "preferences";

export const ALL_CONSENT_CATEGORIES: ConsentCategory[] = [
    "necessary",
    "analytics",
    "marketing",
    "preferences",
];

export const NECESSARY_ONLY: ConsentCategory[] = ["necessary"];

// Type for the cookie consent JSON structure (works for both pp_user_consent and pressidium_cookie_consent)
type ConsentCookie = {
    categories?: string[];
    level?: string[];
    revision?: number;
    consent_date?: string;
    consent_uuid?: string;
    last_consent_update?: string;
    last_update?: string;
};

/**
 * Gets a cookie value by name
 */
export function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Sets a cookie with the given name, value, and expiry days
 */
export function setCookie(name: string, value: string, days: number = 365): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

/**
 * Deletes a cookie by name
 */
export function deleteCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

/**
 * Parses a consent cookie value and returns the categories array
 */
function parseConsentCookie(raw: string): string[] {
    try {
        const obj = JSON.parse(raw) as ConsentCookie;
        return (obj.categories || []).map((s) => s.toLowerCase());
    } catch {
        // Fallback: try parsing as comma-separated string for backward compatibility
        return raw
            .toLowerCase()
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
    }
}

/**
 * Gets the list of consented categories (lowercase)
 * Uses pressidium_cookie_consent for compatibility with WordPress
 */
export function getConsentedCategories(): string[] {
    const consent = getCookie("pressidium_cookie_consent");
    if (consent) {
        return parseConsentCookie(consent);
    }
    return [];
}

/**
 * Future: Gets consent from pp_user_consent cookie
 * Kept for potential future use as an alternative consent source
 */
// export function getConsentedCategoriesFromPpUserConsent(): string[] {
//     const consent = getCookie("pp_user_consent");
//     if (consent) {
//         return parseConsentCookie(consent);
//     }
//     return [];
// }

/**
 * Saves consent preferences to cookie
 * Uses pressidium_cookie_consent format for WordPress compatibility
 */
export function saveConsentPreferences(categories: ConsentCategory[]): void {
    // Always include necessary
    const finalCategories = categories.includes("necessary")
        ? categories
        : ["necessary" as ConsentCategory, ...categories];

    // Build the JSON structure matching pressidium_cookie_consent format
    const consentData: ConsentCookie = {
        categories: finalCategories,
        consent_date: new Date().toISOString(),
        last_consent_update: new Date().toISOString(),
    };

    // Set the cookie using the WordPress Pressidium format
    setCookie("pressidium_cookie_consent", JSON.stringify(consentData), 365);

    // Dispatch a custom event so other parts of the app can react
    window.dispatchEvent(
        new CustomEvent("consentUpdated", { detail: { categories: finalCategories } }),
    );
}

/**
 * Accepts all cookie categories
 */
export function acceptAllCookies(): void {
    saveConsentPreferences([...ALL_CONSENT_CATEGORIES]);
}

/**
 * Accepts only necessary cookies
 */
export function acceptNecessaryOnly(): void {
    saveConsentPreferences([...NECESSARY_ONLY]);
}

/**
 * Checks if the user has granted analytics consent
 */
export function hasAnalyticsConsent(): boolean {
    const categories = getConsentedCategories();
    return categories.includes("analytics");
}

/**
 * Checks if the user has granted marketing/advertising consent
 */
export function hasMarketingConsent(): boolean {
    const categories = getConsentedCategories();
    return categories.includes("marketing") || categories.includes("targeting");
}

/**
 * Checks if the user has granted preferences/functionality consent
 */
export function hasPreferencesConsent(): boolean {
    const categories = getConsentedCategories();
    return categories.includes("preferences") || categories.includes("functionality");
}

/**
 * Checks if any consent has been recorded (user has interacted with consent banner)
 */
export function hasConsentBeenRecorded(): boolean {
    return getCookie("pressidium_cookie_consent") !== null;
}
